import type { UseQueryArgs, UseQueryState } from 'urql';
import { useQuery } from 'urql';

import { DataProviderQueryStatus } from '../types';

/**
 * Best-effort technical string for logging/telemetry (e.g. global AI chart preview).
 * Standard chart error UI omits this and shows generic copy instead.
 */
export function formatProviderErrorMessage(error: unknown): string | undefined {
  if (error === null || error === undefined) {
    return undefined;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error && error.message.trim() !== '') {
    return error.message;
  }
  const r = error as {
    graphQLErrors?: readonly { message?: string }[];
    networkError?: { message?: string };
    message?: string;
  };
  const gq = r.graphQLErrors
    ?.map((e) => e.message)
    .filter((m): m is string => typeof m === 'string' && m.trim() !== '')
    .join('; ');
  if (gq) {
    return gq;
  }
  if (
    r.networkError &&
    typeof r.networkError.message === 'string' &&
    r.networkError.message.trim() !== ''
  ) {
    return r.networkError.message;
  }
  if (typeof r.message === 'string' && r.message.trim() !== '') {
    return r.message;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export const useUrqlQueryWithStatus = <
  Data = unknown,
  Variables extends object = object,
>(
  args: UseQueryArgs<Variables, Data>
) => {
  const [urqlState, executeQuery] = useQuery<Data, Variables>(args);
  const { data, fetching, error } = urqlState;

  // Derive status from urql result state. GraphQL may return partial `data` with
  // top-level `errors`; urql still sets `error` in that case. Prefer Success when
  // we have any usable payload so charts render instead of showing Data Unavailable,
  // but fall through to Error when every top-level field is null so a true failure
  // (e.g. `{ composition_v3: null }` + errors) still surfaces the error UI.
  const status = (() => {
    if (fetching) {
      return DataProviderQueryStatus.Fetching;
    }
    const dataHasUsablePayload =
      data !== undefined &&
      data !== null &&
      (typeof data !== 'object' ||
        Object.values(data).some((v) => v !== null && v !== undefined));
    if (dataHasUsablePayload) {
      return DataProviderQueryStatus.Success;
    }
    if (error) {
      return DataProviderQueryStatus.Error;
    }
    if (data !== undefined && data !== null) {
      return DataProviderQueryStatus.Success;
    }
    return DataProviderQueryStatus.Idle;
  })();

  // Return enhanced state with status
  const enhancedState: UseQueryState<Data, Variables> & {
    status: DataProviderQueryStatus;
  } = {
    ...urqlState,
    status,
  };

  return [enhancedState, executeQuery] as const;
};

export const combineQueryStatus = (
  statuses: DataProviderQueryStatus[] = []
): DataProviderQueryStatus => {
  if (statuses.includes(DataProviderQueryStatus.Error)) {
    return DataProviderQueryStatus.Error;
  }
  if (statuses.includes(DataProviderQueryStatus.Fetching)) {
    return DataProviderQueryStatus.Fetching;
  }
  if (statuses.every((status) => status === DataProviderQueryStatus.Success)) {
    return DataProviderQueryStatus.Success;
  }
  return DataProviderQueryStatus.Idle;
};
